import type { DeliveryWindowOutputRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { BookingAddress, SelectedSalesItemsCar } from 'noddi-types';
import { groupCars } from 'noddi-util';
import { useAuthContext, useShouldFireRequestForLoggedInUser } from 'noddi-web';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import { useMobileBookingCustomer } from './useMobileBookingCustomer';
import { useOrganizationCheckout } from './useOrganizationCheckout';

type Args = {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  /** Absent for a booking without a customer visit: it takes its wheel pickup's day. */
  bookingTimeWindow: DeliveryWindowOutputRecord | undefined;
  selectedAddress: BookingAddress;
};

/** Home-delivery summary data: service department, payment options, the organization checkout and the cart. */
export function useMobileBookingCheckout({ selectedSalesItemsCars, bookingTimeWindow, selectedAddress }: Args) {
  const { isLoggedIn, userData } = useAuthContext();
  const { currentBrand } = useCurrentBrand();
  const { userGroupId } = useMobileBookingCustomer();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();

  const {
    data: serviceDepartmentForBooking,
    isPending: isServiceDepartmentForBookingPending,
    error: serviceDepartmentForBookingError,
    isFetching: isServiceDepartmentForBookingFetching,
    refetch: refetchServiceDepartmentForBooking
  } = noddiAsync.useGet({
    type: URLKeys.getServiceDepartmentFromBookingSummary,
    input: {
      addressId: selectedAddress.id,
      salesItemsIds: selectedSalesItemsCars.map((car) => car.salesItemId)
    }
  });

  const serviceOrganizationId = serviceDepartmentForBooking?.serviceOrganization.id;
  const serviceDepartmentId = serviceDepartmentForBooking?.id;
  const licensePlates = uniqueLicensePlates(selectedSalesItemsCars);

  const {
    data: paymentOptionsData,
    isLoading: isPaymentOptionsPending,
    error: paymentOptionsError,
    isFetching: isPaymentOptionsFetching,
    refetch: refetchPaymentOptions
  } = noddiAsync.useGet({
    type: URLKeys.getPaymentOptions,
    input: {
      userGroupId: userGroupId!,
      serviceOrganizationId: serviceOrganizationId!,
      serviceDepartmentId,
      licensePlateNumbers: licensePlates.length > 0 ? licensePlates.map((plate) => plate.number) : undefined,
      licensePlateCountryCodes: licensePlates.length > 0 ? licensePlates.map((plate) => plate.countryCode) : undefined
    },
    queryConfig: {
      enabled: shouldFireRequest && !!serviceOrganizationId && serviceDepartmentId != null
    }
  });

  const bookingUserGroup = userData?.user.userGroups.find((group) => group.id === userGroupId);
  const organization = useOrganizationCheckout({
    isLoggedIn,
    memberUserGroupId: userGroupId ?? null,
    // The signed-in organization is that customer's booking party, as a picked organization is at a workshop.
    memberOrganization: bookingUserGroup?.isOrganization
      ? { userGroupId: bookingUserGroup.id, name: bookingUserGroup.name }
      : null,
    licensePlateNumbers: licensePlates.map((plate) => plate.number),
    paymentOptions: paymentOptionsData,
    fallbackNames: [bookingUserGroup?.name, userData && `${userData.user.firstName} ${userData.user.lastName}`]
  });

  const {
    data: shoppingCart,
    isPending: isShoppingCartPending,
    error: shoppingCartError,
    isFetching: isShoppingCartFetching,
    refetch: refetchShoppingCart
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.postGetShoppingCartForNewBooking,
    input: {
      addressId: selectedAddress.id,
      cars: groupCars(selectedSalesItemsCars),
      deliveryWindow: bookingTimeWindow && {
        id: bookingTimeWindow.deliveryWindowId,
        endsAt: bookingTimeWindow.endsAt,
        startsAt: bookingTimeWindow.startsAt
      },
      userGroupId: organization.bookingParty.userGroupId ?? userGroupId,
      brandDomain: currentBrand
    },
    queryConfig: {
      staleTime: Infinity
    }
  });

  // The service department feeds the payment options query: without either,
  // checkout silently defaults to pay-before, even for invoice customers.
  const summaryError =
    shoppingCartError ?? serviceDepartmentForBookingError ?? paymentOptionsError ?? organization.carDefaultsError;
  const retrySummary = () => {
    if (shoppingCartError) {
      void refetchShoppingCart();
    }
    if (serviceDepartmentForBookingError) {
      void refetchServiceDepartmentForBooking();
    }
    if (paymentOptionsError) {
      void refetchPaymentOptions();
    }
    if (organization.carDefaultsError) {
      void organization.refetchCarDefaults();
    }
  };
  const isRetrying =
    isShoppingCartFetching ||
    isServiceDepartmentForBookingFetching ||
    isPaymentOptionsFetching ||
    organization.isCarDefaultsFetching;
  const shared = {
    ...organization,
    isLoggedIn,
    userGroupId,
    serviceDepartmentForBooking,
    licensePlates,
    summaryError,
    retrySummary,
    isRetrying
  };

  if (
    isShoppingCartPending ||
    shoppingCartError ||
    isServiceDepartmentForBookingPending ||
    isPaymentOptionsPending ||
    organization.isCarDefaultsLoading ||
    summaryError
  ) {
    return { ...shared, isReady: false as const };
  }

  return { ...shared, isReady: true as const, shoppingCart: shoppingCart.data };
}

/** One row per plate. A car with several services must not look like disagreeing recipients. */
function uniqueLicensePlates(cars: SelectedSalesItemsCar[]) {
  return [
    ...new Map(
      cars.map((car) => [`${car.licensePlate.countryCode}:${car.licensePlate.number}`, car.licensePlate])
    ).values()
  ];
}
